import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { Link as RouterLink } from 'react-router';
import { statusTone, type Product, type Tone } from '@infinix/tokens';
import { tint, v } from './format';

const toneVar: Record<Tone, string> = {
  success: v('success'),
  info: v('info'),
  warning: v('warning'),
  danger: v('danger'),
  neutral: v('dim'),
};

export function toneOf(status: string): Tone {
  return statusTone[status] ?? 'neutral';
}

/** Status pill: coloured text on a 12% tint with a 6px dot. Colour follows the shared vocabulary. */
export function StatusPill({ status, tone, size = 11.5 }: { status: string; tone?: Tone; size?: number }) {
  const c = toneVar[tone ?? toneOf(status)];
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: size, fontWeight: 600, color: c,
        background: tint(c, 12), padding: '3px 9px 3px 8px', borderRadius: '20px', whiteSpace: 'nowrap', lineHeight: 1.35,
      }}
    >
      <Box component="span" aria-hidden sx={{ width: 6, height: 6, borderRadius: '50%', background: c }} />
      {status}
    </Box>
  );
}

export function ProductBadge({ name }: { name: Product }) {
  const c = v(`product-${name.toLowerCase()}`);
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex', fontSize: 11.5, fontWeight: 600, color: c, background: tint(c, 10),
        boxShadow: `0 0 0 1px ${tint(c, 24)} inset`, padding: '2px 9px', borderRadius: '6px', whiteSpace: 'nowrap',
      }}
    >
      {name}
    </Box>
  );
}

export type CardBrand = 'Visa' | 'Mastercard' | 'Amex' | 'Discover';
export type Wallet = 'Apple Pay' | 'Google Pay';

const brandStyle: Record<CardBrand, { label: string; bg: string; fg: string; italic?: boolean }> = {
  Visa: { label: 'VISA', bg: '#F4F6FB', fg: '#1A2B8C', italic: true },
  Mastercard: { label: 'MC', bg: '#F4F6FB', fg: '#C2410C' },
  Amex: { label: 'AMEX', bg: '#2F6FB5', fg: '#FFFFFF' },
  Discover: { label: 'DISC', bg: '#F4F6FB', fg: '#C2410C' },
};

export function BrandChip({ brand }: { brand: CardBrand }) {
  const s = brandStyle[brand];
  return (
    <Box
      component="span"
      aria-label={brand}
      sx={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: 18, minWidth: 34, px: '5px',
        borderRadius: '4px', background: s.bg, color: s.fg, fontSize: 9.5, fontWeight: 800, letterSpacing: '0.02em',
        fontStyle: s.italic ? 'italic' : 'normal', boxShadow: `0 0 0 1px ${v('border2')} inset`,
      }}
    >
      {s.label}
    </Box>
  );
}

export function WalletChip({ wallet }: { wallet: Wallet }) {
  const apple = wallet === 'Apple Pay';
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex', alignItems: 'center', height: 18, px: '6px', borderRadius: '4px', fontSize: 9.5, fontWeight: 700,
        background: apple ? '#0B0B0D' : '#FFFFFF', color: apple ? '#FFFFFF' : '#3C4043',
        boxShadow: `0 0 0 1px ${apple ? '#3A3F4C' : '#DADCE0'} inset`, whiteSpace: 'nowrap',
      }}
    >
      {wallet}
    </Box>
  );
}

/** "[VISA][Apple Pay] Visa ••1209" */
export function MethodCell({ brand, last4, wallet }: { brand: CardBrand; last4: string; wallet?: Wallet | undefined }) {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
      <BrandChip brand={brand} />
      {wallet && <WalletChip wallet={wallet} />}
      <Mono size={12.5} color="dim">{`${brand} ••${last4}`}</Mono>
    </Box>
  );
}

export function Mono({ children, size = 12.5, color = 'text', weight = 500 }: { children: ReactNode; size?: number; color?: string; weight?: number }) {
  return (
    <Box component="span" className="ix-mono" sx={{ fontSize: size, color: v(color), fontWeight: weight, whiteSpace: 'nowrap' }}>
      {children}
    </Box>
  );
}

/** Mono ID styled as a link (accent text). */
export function IdLink({ to, children, size = 12.5 }: { to: string; children: ReactNode; size?: number }) {
  return (
    <Box
      component={RouterLink}
      to={to}
      className="ix-mono"
      sx={{ fontSize: size, color: v('accentText'), fontWeight: 500, textDecoration: 'none', whiteSpace: 'nowrap', '&:hover': { textDecoration: 'underline' } }}
    >
      {children}
    </Box>
  );
}

export function TextLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Box component={RouterLink} to={to} sx={{ color: v('accentText'), fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
      {children}
    </Box>
  );
}

/**
 * "Coming soon" marker. The reason names what it waits on (Core capability or decision) and is
 * reachable by keyboard and screen readers, not only by hover.
 */
export function SoonBadge({ reason, label = 'Soon' }: { reason: string; label?: string }) {
  return (
    <Tooltip title={`Coming soon — ${reason}`}>
      <Box
        component="span"
        tabIndex={0}
        aria-label={`Coming soon: ${reason}`}
        sx={{
          display: 'inline-flex', alignItems: 'center', height: 18, px: '7px', borderRadius: '6px', fontSize: 10.5, fontWeight: 700,
          letterSpacing: '0.04em', textTransform: 'uppercase', color: v('caption'), background: v('sunk'),
          boxShadow: `0 0 0 1px ${v('border2')} inset`, cursor: 'help', outlineOffset: 2,
        }}
      >
        {label}
      </Box>
    </Tooltip>
  );
}

export function Avatar({ initials, size = 30 }: { initials: string; size?: number }) {
  return (
    <Box
      aria-hidden
      sx={{
        width: size, height: size, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: size * 0.38, fontWeight: 700, color: v('accentText'), background: tint(v('accent'), 16),
        boxShadow: `0 0 0 1px ${tint(v('accent'), 30)} inset`,
      }}
    >
      {initials}
    </Box>
  );
}

/** Text for screen readers only (table action headers, status words next to icons). */
export function VisuallyHidden({ children }: { children: ReactNode }) {
  return (
    <Box component="span" sx={{ position: 'absolute', width: '1px', height: '1px', p: 0, m: '-1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0 }}>
      {children}
    </Box>
  );
}
